import { z } from 'zod';
import { MoneySchema, TimestampSchema } from './common.js';
import { AirtimeAmountSchema } from './payments.js';

/**
 * Auto top-up (docs/28 B3; doc 15 §8).
 *
 * A prepaid client saves a card with PayFast while buying airtime, and agrees to be
 * charged a fixed amount whenever its balance falls below a level it chooses, at most
 * so many times a day. Only PayFast's notification credits the balance.
 */

export const AUTOTOPUP_MAX_PER_DAY = 10;

const Threshold = z.string().trim()
  .regex(/^\d{1,5}(\.\d{1,2})?$/, 'An amount in rand, such as 100')
  .refine((v) => Number(v) > 0, 'More than R0');

export const AutoTopupSetupSchema = z.object({
  threshold: Threshold,
  amount: AirtimeAmountSchema,
  max_per_day: z.number().int().min(1).max(AUTOTOPUP_MAX_PER_DAY),
  /** The client ticked the agreement shown to it, in the words consentText gives. */
  consent: z.literal(true),
}).strict();
export type AutoTopupSetup = z.input<typeof AutoTopupSetupSchema>;

/**
 * Pause or resume. Another level, amount or daily limit is another agreement: turn
 * auto top-up off and set it up again.
 */
export const AutoTopupUpdateSchema = z.object({ enabled: z.boolean() }).strict();
export type AutoTopupUpdate = z.input<typeof AutoTopupUpdateSchema>;

export const AutoTopupSchema = z.object({
  /** Whether this account can have it: prepaid, and paying a merchant with a passphrase. */
  available: z.boolean(),
  unavailable_reason: z.string().nullable(),
  /** Who charges the card, for the agreement's words. */
  issuer_name: z.string().nullable(),
  enabled: z.boolean(),
  threshold: MoneySchema.nullable(),
  amount: MoneySchema.nullable(),
  max_per_day: z.number().int().nullable(),
  card: z.object({
    /** pending: the checkout that saves it has not been confirmed by PayFast yet. */
    status: z.enum(['pending', 'active']),
    mode: z.enum(['sandbox', 'live']),
    saved_at: TimestampSchema.nullable(),
    /** What the client agreed to, word for word. */
    consent_text: z.string(),
  }).nullable(),
  last_failure: z.object({ at: TimestampSchema, error: z.string() }).nullable(),
  /** Auto top-ups charged, or on their way, today. */
  today: z.number().int(),
});
export type AutoTopup = z.infer<typeof AutoTopupSchema>;

/** The agreement a client ticks, kept word for word. */
export const consentText = (issuer: string, amount: string, threshold: string, perDay: number): string =>
  `I authorise ${issuer} to charge this card R${amount} each time my airtime balance falls below `
  + `R${threshold}, at most ${perDay} ${perDay === 1 ? 'time' : 'times'} a day, until I turn auto top-up off.`;
